import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { StickerQR } from './StickerQR';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URL
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  try {
    if (url.includes('%')) return url;
    return encodeURI(url);
  } catch {
    return url;
  }
};

const WindowSticker = ({ vehicleId, stockNumber, onClose, vehicleData = null, onPrinted }) => {
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [newEquipment, setNewEquipment] = useState('');
  const [vinDecoding, setVinDecoding] = useState(false);
  const [editablePrice, setEditablePrice] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [garanties, setGaranties] = useState({
    classe: 'D',
    vendeur: 'Demandez',
    manufacturierBase: 'Demandez'
  });

  const garantieClasses = {
    'A': '6 mois ou 10 000 km',
    'B': '3 mois ou 5 000 km',
    'C': '1 mois ou 1 700 km',
    'D': 'Pas de garantie (LPC 37,38)'
  };

  // Canadian Manufacturer Warranty Database (Base / Powertrain in years/km)
  // Format: { base: "X ans / XX 000 km", powertrain: "X ans / XX 000 km" }
  const manufacturerWarranties = {
    // North American
    'Chevrolet': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'GMC': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Buick': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Cadillac': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Ford': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Lincoln': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Dodge': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Jeep': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Ram': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Chrysler': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    // Japanese
    'Toyota': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Lexus': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Honda': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Acura': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Nissan': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Infiniti': { base: '4 ans / 80 000 km', powertrain: '6 ans / 110 000 km' },
    'Mazda': { base: '3 ans / illimité', powertrain: '5 ans / illimité' },
    'Subaru': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Mitsubishi': { base: '5 ans / 100 000 km', powertrain: '10 ans / 160 000 km' },
    'Suzuki': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    // Korean
    'Hyundai': { base: '5 ans / 100 000 km', powertrain: '5 ans / 100 000 km' },
    'Kia': { base: '5 ans / 100 000 km', powertrain: '5 ans / 100 000 km' },
    'Genesis': { base: '5 ans / 100 000 km', powertrain: '5 ans / 100 000 km' },
    // German
    'Volkswagen': { base: '4 ans / 80 000 km', powertrain: '5 ans / 100 000 km' },
    'VW': { base: '4 ans / 80 000 km', powertrain: '5 ans / 100 000 km' },
    'Audi': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'BMW': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Mercedes-Benz': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Mercedes': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Porsche': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Mini': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    // European
    'Volvo': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Land Rover': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Jaguar': { base: '5 ans / 100 000 km', powertrain: '5 ans / 100 000 km' },
    'Alfa Romeo': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Fiat': { base: '3 ans / 60 000 km', powertrain: '5 ans / 100 000 km' },
    'Maserati': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    // Electric/Specialty
    'Tesla': { base: '4 ans / 80 000 km', powertrain: '8 ans / 160 000 km' },
    'Rivian': { base: '5 ans / 100 000 km', powertrain: '5 ans / 100 000 km' },
    'Lucid': { base: '4 ans / 80 000 km', powertrain: '4 ans / 80 000 km' },
    'Polestar': { base: '4 ans / 80 000 km', powertrain: '8 ans / 160 000 km' },
  };

  // Get manufacturer warranty based on make and calculate remaining coverage
  const getManufacturerWarranty = (make, year, mileage) => {
    if (!make || !year) return 'Demandez';
    
    const warranty = manufacturerWarranties[make];
    if (!warranty) return 'Vérifier avec le fabricant';
    
    const currentYear = new Date().getFullYear();
    const vehicleAge = currentYear - year;
    const km = parseInt(mileage) || 0;
    
    // Parse warranty years from base warranty string (e.g., "3 ans / 60 000 km")
    const baseMatch = warranty.base.match(/(\d+)\s*ans/);
    const baseKmMatch = warranty.base.match(/(\d+)\s*000\s*km/);
    const powertrainMatch = warranty.powertrain.match(/(\d+)\s*ans/);
    const powertrainKmMatch = warranty.powertrain.match(/(\d+)\s*000\s*km/);
    
    const baseYears = baseMatch ? parseInt(baseMatch[1]) : 0;
    const baseKm = baseKmMatch ? parseInt(baseKmMatch[1]) * 1000 : Infinity;
    const powertrainYears = powertrainMatch ? parseInt(powertrainMatch[1]) : 0;
    const powertrainKm = powertrainKmMatch ? parseInt(powertrainKmMatch[1]) * 1000 : Infinity;
    
    // Check if warranties are still valid
    const baseValid = vehicleAge < baseYears && km < baseKm;
    const powertrainValid = vehicleAge < powertrainYears && km < powertrainKm;
    
    if (baseValid && powertrainValid) {
      return `Base: ${warranty.base} / Groupe motoprop.: ${warranty.powertrain}`;
    } else if (powertrainValid) {
      return `Base: Expirée / Groupe motoprop.: ${warranty.powertrain}`;
    } else {
      return 'Garanties manufacturier expirées';
    }
  };

  // Auto-calculate warranty category based on Quebec law (since April 5, 2024)
  const calculateWarrantyCategory = (vehicleYear, mileage) => {
    const currentYear = new Date().getFullYear();
    const vehicleAge = currentYear - vehicleYear;
    const km = parseInt(mileage) || 0;

    // Category A: 4 years or less AND max 80,000 km → 6 months or 10,000 km
    if (vehicleAge <= 4 && km <= 80000) {
      return 'A';
    }
    // Category B: 5 years or less AND max 100,000 km → 3 months or 5,000 km
    if (vehicleAge <= 5 && km <= 100000) {
      return 'B';
    }
    // Category C: 7 years or less AND max 120,000 km → 1 month or 1,700 km
    if (vehicleAge <= 7 && km <= 120000) {
      return 'C';
    }
    // Category D: More than 7 years OR more than 120,000 km → No warranty
    return 'D';
  };
  
  // Options for Utilisation Antérieure
  const utilisationTypeOptions = [
    'Véhicule de promenade',
    'Ambulance',
    'Auto-école',
    'Courtoisie',
    'Démontrateur',
    'N/A',
    'Police',
    'Taxi',
    'Véhicule de location'
  ];
  
  const proprietaireOptions = [
    'Particulier',
    'Commercial'
  ];
  
  const [utilisation, setUtilisation] = useState({
    type: 'Véhicule de promenade',
    proprietaire: 'Particulier'
  });

  // Common equipment checklist
  const commonEquipment = [
    'Climatisation',
    'Régulateur de vitesse',
    'Caméra de recul',
    'Télédéverrouillage',
    'Verrouillage électrique',
    'Miroir(s) électrique(s)',
    'Fenêtres électriques',
    'Sièges chauffants',
    'Volant chauffant',
    'Démarreur à distance',
    'Bluetooth',
    'Système de navigation',
    'Toit ouvrant',
    'Sièges en cuir',
    'Système audio premium',
    'Détection angles morts',
    'Assistance au stationnement',
    'Contrôle de stabilité',
    'ABS aux 4 roues',
    'Coussins gonflables multiples',
    'Système antipatinage',
    'Contrôle de traction',
    'Freinage d\'urgence automatique',
    'Avertissement de collision',
    'Régulateur de vitesse adaptatif'
  ];

  useEffect(() => {
    // If vehicleData is passed directly (for bulk print), use it
    if (vehicleData) {
      setVehicle(vehicleData);
      setEditablePrice(vehicleData.regular_price || vehicleData.price || '');
      const warrantyCategory = calculateWarrantyCategory(vehicleData.year, vehicleData.mileage);
      const mfrWarranty = getManufacturerWarranty(vehicleData.make, vehicleData.year, vehicleData.mileage);
      setGaranties(prev => ({ 
        ...prev, 
        classe: warrantyCategory,
        manufacturierBase: mfrWarranty
      }));
      if (vehicleData.vin) {
        decodeVIN(vehicleData.vin);
      }
      setLoading(false);
    } else {
      fetchVehicleDataFromAPI();
    }
  }, [vehicleId, stockNumber, vehicleData]);

  const fetchVehicleDataFromAPI = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      let fetchedData = null;
      
      // Try fetching by vehicle ID first
      if (vehicleId) {
        const response = await fetch(`${API}/api/vehicles/${vehicleId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          fetchedData = await response.json();
        }
      }
      
      // If not found, try by stock number
      if (!fetchedData && stockNumber) {
        const response = await fetch(`${API}/api/fb-bot/vehicle/${stockNumber}`);
        if (response.ok) {
          const data = await response.json();
          // Map fb-bot format to standard format
          fetchedData = {
            year: data.annee,
            make: data.marque,
            model: data.modele,
            trim: data.trim,
            vin: data.vin,
            mileage: data.km,
            price: data.online_price,
            exterior_color: data.couleur,
            transmission: data.transmission,
            stock_number: data.stock
          };
        }
      }
      
      if (fetchedData) {
        setVehicle(fetchedData);
        setEditablePrice(fetchedData.regular_price || fetchedData.price || '');
        
        // Auto-calculate warranty category based on year and mileage
        const warrantyCategory = calculateWarrantyCategory(fetchedData.year, fetchedData.mileage);
        
        // Auto-get manufacturer warranty based on make, year, and mileage
        const mfrWarranty = getManufacturerWarranty(fetchedData.make, fetchedData.year, fetchedData.mileage);
        
        setGaranties(prev => ({ 
          ...prev, 
          classe: warrantyCategory,
          manufacturierBase: mfrWarranty
        }));
        
        // Decode VIN if available
        if (fetchedData.vin) {
          await decodeVIN(fetchedData.vin);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      setLoading(false);
    }
  };

  const decodeVIN = async (vin) => {
    setVinDecoding(true);
    try {
      // Use our backend (VMR Canada) instead of calling NHTSA directly.
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/vin/decode/${vin}`);
      const data = await response.json();

      if (data && !data.error) {
        const equipmentList = [];
        if (data.body_type === 'suv' || data.body_type === 'truck' || data.body_type === 'van') {
          equipmentList.push('Caméra de recul');
        }
        if (data.transmission === 'cvt' || data.transmission === 'automatic') {
          equipmentList.push('Transmission automatique');
        }
        if (data.drivetrain === 'awd' || data.drivetrain === '4wd') {
          equipmentList.push('Traction intégrale');
        }
        if (data.fuel_type === 'hybrid' || data.fuel_type === 'electric') {
          equipmentList.push('Motorisation écoénergétique');
        }
        equipmentList.push('Coussins gonflables');
        equipmentList.push('ABS aux 4 roues');
        equipmentList.push('Contrôle de stabilité');
        equipmentList.push('Climatisation');
        equipmentList.push('Télédéverrouillage');
        equipmentList.push('Verrouillage électrique');
        equipmentList.push('Miroir(s) électrique(s)');
        setEquipment(equipmentList);
      }
    } catch (error) {
      console.log('VIN decode error:', error);
    }
    setVinDecoding(false);
  };

  const toggleEquipment = (item) => {
    if (equipment.includes(item)) {
      setEquipment(equipment.filter(e => e !== item));
    } else {
      setEquipment([...equipment, item]);
    }
  };

  const addEquipment = () => {
    if (newEquipment.trim() && !equipment.includes(newEquipment.trim())) {
      setEquipment([...equipment, newEquipment.trim()]);
      setNewEquipment('');
    }
  };

  const removeEquipment = (item) => {
    setEquipment(equipment.filter(e => e !== item));
  };

  const handlePrint = () => {
    window.print();
    onPrinted?.();
  };

  // Auto-print when sticker loads
  useEffect(() => {
    if (!loading && vehicle) {
      const timer = setTimeout(() => {
        window.print();
        onPrinted?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, vehicle]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg">
          <div className="text-center text-red-600">Véhicule non trouvé</div>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const year = vehicle.year || '';
  const make = vehicle.make || '';
  const model = vehicle.model || '';
  const trim = vehicle.trim || '';
  const vin = vehicle.vin || '';
  const stock = vehicle.stock_number || '';
  const mileage = vehicle.mileage || '';
  const color = vehicle.exterior_color || '';
  const transmission = vehicle.transmission || 'Automatique';
  const fuelType = vehicle.fuel_type || 'Essence';
  const mainImage = vehicle.images?.[0] ? normalizeImageUrl(vehicle.images[0]) : null;

  // Portal to <body> so print CSS can drop the whole admin page (it caused dozens of duplicate pages)
  return createPortal(
    <div className="print-root fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto print:static print:bg-white print:block print:inset-auto print:overflow-visible sticker-modal-wrapper">
      <style>{`
        @media print {
          body > *:not(.print-root) {
            display: none !important;
          }

          @page {
            margin: 5mm;
            size: A4 portrait;
          }
          
          * {
            overflow: visible !important;
          }
          
          body * {
            visibility: hidden !important;
          }
          
          .sticker-modal-wrapper,
          .sticker-modal-wrapper * {
            visibility: visible !important;
          }
          
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
          
          .sticker-modal-wrapper {
            position: static !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            height: 100% !important;
            overflow: hidden !important;
          }
          
          .sticker-container {
            max-width: 100% !important;
            margin: 0 !important;
            padding: 3mm !important;
            font-size: 8pt !important;
            box-shadow: none !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            height: auto !important;
          }
          
          .sticker-header-text {
            font-size: 10pt !important;
          }
          
          .sticker-title {
            font-size: 13pt !important;
            margin-bottom: 2px !important;
            padding: 4px 10px !important;
          }
          
          .sticker-section-header {
            padding: 2px 6px !important;
            font-size: 8pt !important;
            margin-bottom: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .sticker-content {
            padding: 3px 5px !important;
            font-size: 7.5pt !important;
            line-height: 1.3 !important;
          }
          
          .sticker-logo {
            height: 50px !important;
          }
          
          .section-spacing {
            margin-bottom: 2px !important;
            page-break-inside: avoid !important;
          }
          
          .grid {
            gap: 0.25rem !important;
          }
          
          .border-b-2 {
            border-width: 1px !important;
          }
          
          .mb-5, .mb-6, .mb-4 {
            margin-bottom: 3px !important;
          }
          
          .gap-7, .gap-8 {
            gap: 0.25rem !important;
          }
          
          .p-2 {
            padding: 3px !important;
          }
          
          .p-3 {
            padding: 4px !important;
          }
          
          .py-2 {
            padding-top: 2px !important;
            padding-bottom: 2px !important;
          }
          
          .py-3 {
            padding-top: 3px !important;
            padding-bottom: 3px !important;
          }
          
          .pb-3 {
            padding-bottom: 2px !important;
          }
          
          .pt-3 {
            padding-top: 2px !important;
          }
          
          .mt-4 {
            margin-top: 2px !important;
          }
          
          .vehicle-photo {
            max-height: 100px !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .equip-grid-print {
            column-gap: 6px !important;
          }
          
          .equip-grid-print li {
            font-size: 6.5pt !important;
            line-height: 1.2 !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
      
      {/* Control Buttons */}
      <div className="max-w-4xl mx-auto p-4 no-print">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-bold text-white">Étiquette de Vitre</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditor(!showEditor)}
              className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
            >
              {showEditor ? 'Masquer options' : 'Modifier'}
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Imprimer
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Fermer
            </button>
          </div>
        </div>
        
        {/* Editable Fields Section - Hidden by default, toggle with "Modifier" */}
        {showEditor && (
        <div className="bg-white border border-gray-300 rounded-lg mb-4 p-3">
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-3">
              {/* Price Editor */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Prix de Vente</label>
                <input
                  type="text"
                  value={editablePrice}
                  onChange={(e) => setEditablePrice(e.target.value)}
                  placeholder="Entrer le prix..."
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                />
              </div>

              {/* Garanties Editor */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Garanties</label>
                <div className="space-y-1.5">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Coût/Crédit/Taxe/Solde:</label>
                    <select
                      value={garanties.classe}
                      onChange={(e) => setGaranties({...garanties, classe: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                    >
                      <option value="A">A : 6 mois ou 10 000 km</option>
                      <option value="B">B : 3 mois ou 5 000 km</option>
                      <option value="C">C : 1 mois ou 1 700 km</option>
                      <option value="D">D : Pas de garantie (LPC 37,38)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Du vendeur:</label>
                    <input
                      type="text"
                      value={garanties.vendeur}
                      onChange={(e) => setGaranties({...garanties, vendeur: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Manufacturier Base/Motopropulseur:</label>
                    <input
                      type="text"
                      value={garanties.manufacturierBase}
                      onChange={(e) => setGaranties({...garanties, manufacturierBase: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              {/* Utilisation Antérieure Editor */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Utilisation Antérieure</label>
                <div className="space-y-1.5">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Type:</label>
                    <select
                      value={utilisation.type}
                      onChange={(e) => setUtilisation({...utilisation, type: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                    >
                      {utilisationTypeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Propriétaire:</label>
                    <select
                      value={utilisation.proprietaire}
                      onChange={(e) => setUtilisation({...utilisation, proprietaire: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                    >
                      {proprietaireOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Custom Equipment */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Ajouter Équipement</label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newEquipment}
                    onChange={(e) => setNewEquipment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addEquipment()}
                    placeholder="Autre équipement..."
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm text-gray-900 bg-gray-50"
                  />
                  <button
                    onClick={addEquipment}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Equipment Checklist */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Équipements - Sélectionner</label>
              <div className="grid grid-cols-2 gap-1 max-h-72 overflow-y-auto bg-gray-50 p-2 rounded border border-gray-200">
                {commonEquipment.map((item) => (
                  <label key={item} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white p-1 rounded text-gray-800">
                    <input
                      type="checkbox"
                      checked={equipment.includes(item)}
                      onChange={() => toggleEquipment(item)}
                      className="w-3.5 h-3.5"
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
              
              {/* Selected Equipment Tags */}
              {equipment.length > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sélectionnés ({equipment.length}):</label>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {equipment.map((item) => (
                      <div key={item} className="bg-blue-100 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                        <span className="text-gray-800">{item}</span>
                        <button
                          onClick={() => removeEquipment(item)}
                          className="text-red-500 hover:text-red-700 font-bold text-sm leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Window Sticker */}
      <div className="max-w-4xl mx-auto bg-white print:max-w-full sticker-container" style={{ fontFamily: 'Arial, sans-serif', padding: '10mm' }}>
        {/* Header with Dark Blue Background */}
        <div className="flex justify-between items-center mb-3 pb-2 px-3 py-2 bg-blue-900 rounded-lg">
          <div className="sticker-header-text">
            <div className="text-xl font-bold text-white">ENTREPÔT AUTO MONTRÉAL</div>
            <div className="text-xs text-white">1007 boul. René-Lévesque VERDUN, H3E 1H5 | (833) 995-6477</div>
          </div>
          <div>
            <img 
              src="/logo512.png" 
              alt="Entrepôt Auto Montréal" 
              className="sticker-logo"
              style={{ height: '85px' }}
            />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-3">
          <h1 className="sticker-title text-2xl font-bold text-white bg-blue-900 py-1.5 px-4 rounded-lg">
            {year} {make} {model} {trim}
          </h1>
        </div>

        {/* Car Photo + Price Row */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          {/* Car Photo Box */}
          <div className="section-spacing">
            <div className="sticker-section-header bg-blue-900 text-white px-3 py-2 font-bold text-base rounded-t-lg">Photo du véhicule</div>
            <div className="sticker-content border-2 border-gray-300 p-2 rounded-b-lg flex items-center justify-center" style={{ minHeight: '160px' }}>
              {mainImage ? (
                <img 
                  src={mainImage} 
                  alt={`${year} ${make} ${model}`}
                  className="max-w-full max-h-40 object-contain rounded-lg shadow-md vehicle-photo"
                  style={{ maxHeight: '150px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="text-gray-400 text-center">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Photo non disponible</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Price + Financing */}
          <div>
            {/* Prix de vente */}
            <div className="section-spacing mb-4">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-2 font-bold text-base rounded-t-lg">Prix de vente</div>
              <div className="sticker-content border-2 border-gray-300 p-3 text-center rounded-b-lg">
                <div className="text-3xl font-bold text-gray-900">
                  {editablePrice ? `${parseFloat(editablePrice).toLocaleString('fr-CA')}$` : 'Sur demande'}
                </div>
              </div>
            </div>

            {/* Financement */}
            <div className="section-spacing">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-2 font-bold text-base rounded-t-lg">Financement</div>
              <div className="sticker-content border-2 border-gray-300 p-2 text-center text-sm rounded-b-lg text-gray-900">
                Demandez au marchand
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-4">
          {/* Left Column */}
          <div>

            {/* Garanties */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Garanties</div>
              <div className="sticker-content border-2 border-gray-300 p-2 text-sm rounded-b-lg text-gray-900">
                <p className="font-bold mb-1">Garantie légale</p>
                <p className="mb-1"><strong>Classe {garanties.classe}:</strong> {garantieClasses[garanties.classe]}</p>
                <p className="mb-1"><strong>Du vendeur:</strong> {garanties.vendeur}</p>
                <p className="font-bold mt-2 mb-1">Manufacturier</p>
                <p className="mb-0"><strong>Base/Motopropulseur:</strong> {garanties.manufacturierBase}</p>
              </div>
            </div>

            {/* Utilisation antérieure */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Utilisation antérieure</div>
              <div className="sticker-content border-2 border-gray-300 p-2 text-sm rounded-b-lg text-gray-900">
                <p className="font-bold mb-1">{utilisation.type}</p>
                <p className="mb-0"><strong>Propriétaire:</strong> {utilisation.proprietaire}</p>
              </div>
            </div>

            {/* Réparations */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Réparations</div>
              <div className="sticker-content border-2 border-gray-300 p-2 text-sm rounded-b-lg text-gray-900">
                <p className="font-bold mb-0">Aucune</p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* Description du véhicule */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Description du véhicule</div>
              <div className="sticker-content border-2 border-gray-300 p-2 text-sm rounded-b-lg text-gray-900">
                <div className="grid grid-cols-2 gap-2" style={{ lineHeight: '1.6' }}>
                  <div><strong>No unité:</strong></div>
                  <div>{stock || 'N/A'}</div>
                  
                  <div><strong>Année:</strong></div>
                  <div>{year || 'N/A'}</div>
                  
                  <div><strong>Marque:</strong></div>
                  <div>{make || 'N/A'}</div>
                  
                  <div><strong>Modèle:</strong></div>
                  <div>{model || 'N/A'}</div>
                  
                  <div><strong>Version:</strong></div>
                  <div>{trim || 'N/A'}</div>
                  
                  <div><strong>N° série:</strong></div>
                  <div className="text-sm break-all">{vin || 'N/A'}</div>
                  
                  <div><strong>Odomètre:</strong></div>
                  <div>{mileage ? `${parseInt(mileage).toLocaleString('fr-CA')} km` : 'N/A'}</div>
                  
                  <div><strong>Transmission:</strong></div>
                  <div>{transmission}</div>
                  
                  <div><strong>Carburant:</strong></div>
                  <div>{fuelType}</div>
                  
                  <div><strong>Couleur ext.:</strong></div>
                  <div>{color || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Équipements */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Équipements</div>
              <div className="sticker-content border-2 border-gray-300 p-2 rounded-b-lg text-gray-900">
                <ul 
                  className={`equip-grid-print ${equipment.length > 12 ? 'text-[10px]' : 'text-xs'}`}
                  style={{ 
                    columns: equipment.length > 12 ? 3 : 2,
                    columnGap: '8px',
                    lineHeight: equipment.length > 12 ? '1.2' : '1.35',
                  }}
                >
                  {equipment.length > 0 ? (
                    equipment.map((item, index) => (
                      <li key={index} style={{ breakInside: 'avoid' }}>• {item}</li>
                    ))
                  ) : (
                    <li className="italic">Ajouter équipements</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Remarques */}
            <div className="section-spacing mb-3">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1.5 font-bold text-sm rounded-t-lg">Remarques</div>
              <div className="sticker-content border-2 border-gray-300 p-1 rounded-b-lg" style={{ minHeight: '24px' }}>
              </div>
            </div>

            {/* Acceptation */}
            <div className="section-spacing mb-1">
              <div className="sticker-section-header bg-blue-900 text-white px-3 py-1 font-bold text-sm rounded-t-lg">Acceptation</div>
              <div className="sticker-content border-2 border-gray-300 p-1.5 text-xs rounded-b-lg text-gray-900">
                <p className="mb-1">Je reconnais avoir reçu une copie de l'étiquette</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs"><strong>Date:</strong></p>
                    <div className="border-b border-black mt-1"></div>
                  </div>
                  <div>
                    <p className="text-xs"><strong>Signature:</strong></p>
                    <div className="border-b border-black mt-1"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-1 pt-1 border-t-2 border-blue-900">
          <div className="flex justify-between items-end">
            <div>
              <p className="font-bold text-xs text-blue-900">www.entrepotautomontreal.ca</p>
              <p className="text-xs italic text-blue-800">« Redéfinir L'expérience Automobile »</p>
              <p className="text-xs text-gray-600 mt-1">* Prix excluent taxes et frais.</p>
            </div>
            <StickerQR vehicleId={vehicle?.id || vehicleId} />
          </div>
        </div>
      </div>
    </div>
  , document.body);
};

export default WindowSticker;
